import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { apiGet, apiPost, apiPut, apiDelete, getErrorMessage } from '../../utils/api'
import { useSocket } from '../../contexts/SocketContext'
import { Badge } from '../shared/Badge'
import { useGroupUnread } from '../../contexts/GroupUnreadContext'
import { CachedDataNote } from '../shared/CachedDataNote'
import { useWhatsAppStatus } from '../../contexts/WhatsAppStatusContext'

/* ─── Types ────────────────────────────────────────────────────────────── */

interface WAGroup {
  id: string
  name: string
  participantsCount?: number
  isAdmin?: boolean
}

interface WAGroupInfo extends WAGroup {
  description?: string
  owner?: string
  createdAt?: number | string
  participants: {
    id: string
    number: string
    name?: string
    isAdmin: boolean
    isSuperAdmin: boolean
  }[]
  isReadOnly?: boolean
}

interface GroupMessage {
  id: string
  chatId: string
  from: string
  to: string
  body?: string
  type: string
  timestamp?: number
  direction: 'INCOMING' | 'OUTGOING'
  metadata?: any
  mediaUrl?: string
  senderName?: string
  isStarred?: boolean
  replyToId?: string
  replyBody?: string
  replySenderName?: string
  replyType?: string
}

interface GroupTag {
  id: string
  name: string
  color: string
  sort_order: number
}

/* ─── WhatsApp icon ────────────────────────────────────────────────────── */

const GroupIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className || 'w-5 h-5'} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
  </svg>
)

/* ─── Group Avatar (shows picture, or colored initials fallback) ──────── */

// WhatsApp-style avatar colors — stable per group name
const AVATAR_COLORS = [
  ['#25D366', '#128C7E'], // WhatsApp green
  ['#34B7F1', '#0088CC'], // Blue
  ['#E91E63', '#C2185B'], // Pink
  ['#9C27B0', '#7B1FA2'], // Purple
  ['#FF9800', '#F57C00'], // Orange
  ['#00BCD4', '#0097A7'], // Teal
  ['#FF5722', '#E64A19'], // Deep orange
  ['#3F51B5', '#303F9F'], // Indigo
  ['#8BC34A', '#689F38'], // Light green
  ['#795548', '#5D4037'], // Brown
]

function getGroupColor(name: string): [string, string] {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length] as [string, string]
}

function getGroupInitials(name: string): string {
  const words = name.trim().split(/\s+/)
  if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase()
  return name.substring(0, 2).toUpperCase()
}

const GroupAvatar: React.FC<{
  pictureUrl?: string
  groupName?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  children?: React.ReactNode // for overlay badges
}> = ({ pictureUrl, groupName = '', size = 'md', children }) => {
  const [imgError, setImgError] = useState(false)
  const sizeClasses = { sm: 'w-8 h-8', md: 'w-10 h-10', lg: 'w-14 h-14', xl: 'w-20 h-20' }
  const fontSizes = { sm: 'text-xs', md: 'text-sm', lg: 'text-lg', xl: 'text-2xl' }
  const [bgColor] = getGroupColor(groupName)
  const initials = getGroupInitials(groupName)

  return (
    <div className={`relative ${sizeClasses[size]} rounded-full shrink-0 overflow-hidden`}>
      {pictureUrl && !imgError ? (
        <img
          src={pictureUrl}
          alt="Group"
          className={`${sizeClasses[size]} rounded-full object-cover`}
          onError={() => setImgError(true)}
        />
      ) : (
        <div
          className={`${sizeClasses[size]} rounded-full flex items-center justify-center`}
          style={{ backgroundColor: bgColor }}
        >
          <span className={`${fontSizes[size]} font-bold text-white leading-none`} style={{ textShadow: '0 1px 2px rgba(0,0,0,0.15)' }}>
            {initials}
          </span>
        </div>
      )}
      {children}
    </div>
  )
}

/* ─── Main Component ───────────────────────────────────────────────────── */

export const GroupsView: React.FC = () => {
  const { socket } = useSocket()
  const { unreadGroups, markGroupRead, clearActiveGroup } = useGroupUnread()
  const { groupId: urlGroupId } = useParams<{ groupId?: string }>()
  const navigate = useNavigate()

  // State
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [groups, setGroups] = useState<WAGroup[]>([])
  const [selectedGroup, setSelectedGroup] = useState<WAGroup | null>(null)
  const [groupInfo, setGroupInfo] = useState<WAGroupInfo | null>(null)
  const [messages, setMessages] = useState<GroupMessage[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [sendingMessage, setSendingMessage] = useState(false)
  const [messageText, setMessageText] = useState('')
  // @-mention picker state
  const [mentionQuery, setMentionQuery] = useState<string | null>(null) // null = picker closed
  const [mentions, setMentions] = useState<Array<{ jid: string; number: string; name: string }>>([])
  const [participantPics, setParticipantPics] = useState<Record<string, string>>({}) // jid -> avatar url
  const [searchQuery, setSearchQuery] = useState('')
  const [showInfo, setShowInfo] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastMessages, setLastMessages] = useState<Record<string, GroupMessage>>({})
  const [contactsMap, setContactsMap] = useState<Record<string, string>>({}) // id -> pushName
  const [activeNotices, setActiveNotices] = useState<{ id: string; name: string; message: string; schedule_type: string; group_ids: string[]; start_date: string | null; end_date: string | null }[]>([])
  const [groupPictures, setGroupPictures] = useState<Record<string, string>>({}) // groupId -> picture URL
  const [pendingFile, setPendingFile] = useState<{ file: File; name: string; size: number; type: string; preview?: string; base64?: string } | null>(null) // media attach state
  const [linkedCustomer, setLinkedCustomer] = useState<{ customer_id: number; company_name: string } | null>(null) // linked customer for the selected group
  // Group JID → paid customer summary, used to render the "linked" indicator
  // next to each group in the left-hand list. Fetched once per view mount
  // (small payload, only one row per *linked* group) and refreshed whenever
  // we relink / unlink from this page.
  const [linkedMap, setLinkedMap] = useState<Record<string, { customer_id: number; customer_name: string; wequote_id: number | null }>>({})

  // ── Tag state ──
  const [allTags, setAllTags] = useState<GroupTag[]>([])
  const [groupTagMap, setGroupTagMap] = useState<Record<string, { tag_id: string; name: string; color: string }[]>>({}) // groupId -> tags
  const [activeTagFilters, setActiveTagFilters] = useState<string[]>([]) // tag IDs currently filtering
  const [showTagManager, setShowTagManager] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [newTagColor, setNewTagColor] = useState('#2563eb')
  const [editingTag, setEditingTag] = useState<GroupTag | null>(null)
  const [showTagAssign, setShowTagAssign] = useState(false) // toggle tag assignment dropdown in info panel
  const [editingMessage, setEditingMessage] = useState<GroupMessage | null>(null) // message being edited
  const [editText, setEditText] = useState('')
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; msg: GroupMessage } | null>(null)
  const [replyTo, setReplyTo] = useState<GroupMessage | null>(null) // message being replied to
  const [forwardMsg, setForwardMsg] = useState<GroupMessage | null>(null) // message to forward
  const [forwardSearch, setForwardSearch] = useState('') // forward modal search
  const [forwardTarget, setForwardTarget] = useState<string | null>(null) // selected group id to forward to
  const [forwardSending, setForwardSending] = useState(false) // sending state
  const [forwardSent, setForwardSent] = useState(false) // success state
  const [deleteMode, setDeleteMode] = useState(false) // multi-select delete mode
  const [selectedMsgIds, setSelectedMsgIds] = useState<Set<string>>(new Set()) // selected message IDs for deletion
  const [isDeleting, setIsDeleting] = useState(false) // deleting in progress
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null) // track hovered message for dropdown arrow

  // ── Media Gallery state ──
  const [mediaItems, setMediaItems] = useState<any[]>([])
  const [mediaCounts, setMediaCounts] = useState<{ image: number; video: number; document: number; total: number }>({ image: 0, video: 0, document: 0, total: 0 })
  const [mediaTab, setMediaTab] = useState<'image' | 'video' | 'document'>('image')
  const [mediaLoading, setMediaLoading] = useState(false)
  const [showFullGallery, setShowFullGallery] = useState(false) // full-page gallery overlay
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null) // image lightbox
  const [mediaExpanded, setMediaExpanded] = useState(false) // collapsed by default

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const editInputRef = useRef<HTMLTextAreaElement>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  // Clear active group when leaving the groups page (component unmount)
  useEffect(() => {
    return () => { clearActiveGroup() }
  }, [clearActiveGroup])

  /* ── Track active session ID via the global status context ─────────
   *
   * The previous implementation did a one-shot fetch of /whatsapp/status here
   * and showed a hard "No active WhatsApp session" error if activeSession came
   * back null. That was racy — OpenWA's listSessions() call occasionally
   * returns success-but-empty for a single tick (busy handling inbound events,
   * GC, transient timeout). Every 3-4 page loads would land on an unlucky
   * tick and the page would refuse to load until manual refresh.
   *
   * The global useWhatsAppStatus hook already polls /status every 20s and
   * self-heals from these flakes. We just read the latest sessionId off it.
   * If activeSession is briefly missing on first render we just wait for the
   * next poll instead of breaking the page.
   */
  const waStatus = useWhatsAppStatus()
  useEffect(() => {
    if (waStatus.activeSession?.id && waStatus.activeSession.id !== sessionId) {
      setSessionId(waStatus.activeSession.id)
    }
  }, [waStatus.activeSession?.id, sessionId])

  /* ── Fetch groups list ────────────────────────────────────────────── */
  // Three paths, in order of preference:
  //   1. sessionId is known        → live fetch, falls back to cached on error
  //   2. sessionId not known yet   → go straight to cached (still useful while
  //                                   we wait for the next status poll)
  //   3. cached also fails         → only then surface a hard error
  // This eliminates the "every 4th refresh shows a red banner" flake — even
  // when listSessions briefly returns no active session, the user still sees
  // their groups from the local cache and can keep working. CachedDataNote
  // (rendered in the header) communicates freshness when relevant.
  const fetchGroups = useCallback(async () => {
    setLoading(true)
    const tryCached = async () => {
      const cached = await apiGet<any>('/integrations/whatsapp/groups/cached')
      const groupList = cached.success !== false ? (cached.data || cached) : []
      setGroups(Array.isArray(groupList) ? groupList : [])
      setError(null)
    }
    try {
      if (!sessionId) {
        await tryCached()
        return
      }
      const res = await apiGet<any>('/integrations/whatsapp/groups', { sessionId })
      const groupList = res.success !== false ? (res.data || res) : []
      setGroups(Array.isArray(groupList) ? groupList : [])
      setError(null)
    } catch (err: any) {
      try { await tryCached() }
      catch { setError(getErrorMessage(err, 'Failed to load groups')) }
    } finally {
      setLoading(false)
    }
  }, [sessionId])

  useEffect(() => { fetchGroups() }, [fetchGroups])

  /* ── Fetch the linked-customer map for the Groups list indicator ──── */
  // Tells us which of the 17+ groups are linked to a paid customer so we can
  // render a small badge next to the group name. Re-fetched whenever
  // linkedCustomer changes (i.e. user just linked or unlinked the currently
  // selected group) so the list updates in place without a full reload.
  const fetchLinkedMap = useCallback(async () => {
    try {
      const map = await apiGet<Record<string, any>>('/integrations/whatsapp/groups/linked-map')
      setLinkedMap(map || {})
    } catch {
      setLinkedMap({})
    }
  }, [])
  useEffect(() => { fetchLinkedMap() }, [fetchLinkedMap, linkedCustomer])

  /* ── Fetch group profile pictures ─────────────────────────────────── */
  useEffect(() => {
    if (!sessionId || groups.length === 0) return
    const fetchPictures = async () => {
      const pics: Record<string, string> = {}
      await Promise.allSettled(
        groups.map(async (g) => {
          try {
            const res = await apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(g.id)}/picture`, { sessionId })
            const url = res?.data?.url || res?.url
            if (url) pics[g.id] = url
          } catch {
            // No picture available for this group — will use fallback
          }
        })
      )
      if (Object.keys(pics).length > 0) {
        setGroupPictures(prev => ({ ...prev, ...pics }))
      }
    }
    fetchPictures()
  }, [sessionId, groups])

  /* ── Fetch last message for every group (list previews) ──────────── */
  useEffect(() => {
    if (!sessionId || groups.length === 0) return
    apiGet<{ lastMessages: Record<string, GroupMessage> }>('/integrations/whatsapp/groups/last-messages')
      .then(res => {
        if (res.lastMessages) {
          setLastMessages(prev => ({ ...prev, ...res.lastMessages }))
        }
      })
      .catch(() => { /* optional – fail silently */ })
  }, [sessionId, groups])

  /* ── Fetch active auto-reply notices ─────────────────────────────── */
  useEffect(() => {
    apiGet<{ notices: any[] }>('/wa-group-notices')
      .then(res => {
        const now = new Date()
        const active = (res.notices || []).filter((n: any) => {
          if (!n.is_active) return false
          if (n.schedule_type === 'instant') return true
          if (n.schedule_type === 'scheduled') {
            if (n.start_date && now < new Date(n.start_date)) return false
            if (n.end_date && now > new Date(n.end_date)) return false
            return true
          }
          return false
        })
        setActiveNotices(active.map((n: any) => ({
          id: n.id,
          name: n.name,
          message: n.message,
          schedule_type: n.schedule_type,
          group_ids: Array.isArray(n.group_ids) ? n.group_ids : [],
          start_date: n.start_date,
          end_date: n.end_date,
        })))
      })
      .catch(() => { /* notices are optional */ })
  }, [])

  /* ── Fetch contacts for name resolution ──────────────────────────── */
  useEffect(() => {
    if (!sessionId) return
    apiGet<any>('/integrations/whatsapp/contacts', { sessionId })
      .then((res) => {
        const contacts = Array.isArray(res) ? res : (res.data || [])
        const map: Record<string, string> = {}
        for (const c of contacts) {
          if (c.pushName || c.name) {
            // Map by id (e.g. "447899499452@c.us") and by number
            if (c.id) map[c.id] = c.pushName || c.name
            if (c.number) map[`${c.number}@c.us`] = c.pushName || c.name
          }
        }
        setContactsMap(map)
      })
      .catch(() => { /* contacts are optional, fail silently */ })
  }, [sessionId])

  /* ── Fetch all tags and group-tag assignments ─────────────────────── */
  const fetchTags = useCallback(async () => {
    try {
      const [tagsRes, assignRes] = await Promise.all([
        apiGet<{ tags: GroupTag[] }>('/group-tags'),
        apiGet<{ assignments: Record<string, { tag_id: string; name: string; color: string }[]> }>('/group-tag-assignments'),
      ])
      setAllTags(tagsRes.tags || [])
      setGroupTagMap(assignRes.assignments || {})
    } catch {
      // Tags are optional, fail silently
    }
  }, [])

  useEffect(() => { fetchTags() }, [fetchTags])

  /* ── Tag management helpers ──────────────────────────────────────── */
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return
    try {
      await apiPost('/group-tags', { name: newTagName.trim(), color: newTagColor })
      setNewTagName('')
      setNewTagColor('#2563eb')
      fetchTags()
    } catch (err: any) {
      console.error('Failed to create tag:', err)
    }
  }

  const handleUpdateTag = async (tag: GroupTag) => {
    try {
      await apiPut(`/group-tags/${tag.id}`, { name: tag.name, color: tag.color })
      setEditingTag(null)
      fetchTags()
    } catch (err: any) {
      console.error('Failed to update tag:', err)
    }
  }

  const handleDeleteTag = async (tagId: string) => {
    try {
      await apiDelete(`/group-tags/${tagId}`)
      // Also remove from active filters
      setActiveTagFilters(prev => prev.filter(id => id !== tagId))
      fetchTags()
    } catch (err: any) {
      console.error('Failed to delete tag:', err)
    }
  }

  const handleToggleGroupTag = async (groupId: string, tagId: string) => {
    const currentTags = groupTagMap[groupId] || []
    const hasTag = currentTags.some(t => t.tag_id === tagId)
    if (hasTag) {
      await apiDelete(`/group-tags/assign/${encodeURIComponent(groupId)}/${tagId}`)
    } else {
      await apiPost('/group-tags/assign', { group_id: groupId, tag_id: tagId })
    }
    fetchTags()
  }

  /* ── Fetch group info ─────────────────────────────────────────────── */
  const fetchGroupInfo = useCallback(async (groupId: string) => {
    if (!sessionId) return
    try {
      const res = await apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(groupId)}`, { sessionId })
      const info = res.success !== false ? (res.data || res) : null
      // Enrich participant names from contacts map
      if (info?.participants && Object.keys(contactsMap).length > 0) {
        info.participants = info.participants.map((p: any) => ({
          ...p,
          name: p.name || contactsMap[p.id] || contactsMap[`${p.number}@c.us`] || undefined,
        }))
      }
      setGroupInfo(info)
    } catch {
      setGroupInfo(null)
    }
  }, [sessionId, contactsMap])

  /* ── Fetch messages for selected group ────────────────────────────── */
  const prevMsgIdsRef = useRef<string>('')
  const fetchMessages = useCallback(async (groupId: string, isPolling = false) => {
    if (!sessionId) return
    try {
      // Only show loading spinner on initial fetch, not polling refreshes
      if (!isPolling) setLoadingMessages(true)
      const res = await apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(groupId)}/messages`, {
        sessionId,
        limit: 100,
      })
      // Response can be { messages: [...], total: N } or an array directly
      const raw = res.success !== false ? (res.data || res) : []
      const msgs = Array.isArray(raw) ? raw : (Array.isArray(raw?.messages) ? raw.messages : [])

      // Build a fingerprint of message IDs to detect actual changes. Reactions
      // only change metadata, so their count is included too.
      const newFingerprint = msgs.map((m: any) => `${m.id || m.timestamp}:${(m.metadata && Array.isArray(m.metadata.reactions) ? m.metadata.reactions.length : 0)}`).join('|')
      if (newFingerprint !== prevMsgIdsRef.current) {
        const oldCount = prevMsgIdsRef.current ? prevMsgIdsRef.current.split('|').length : 0
        prevMsgIdsRef.current = newFingerprint
        setMessages(msgs)

        // Only auto-scroll if new messages were added (not on initial load re-renders or same data)
        if (isPolling && msgs.length > oldCount) {
          setTimeout(() => scrollToBottom(), 50)
        }
      }

      // Track last message for group list preview
      if (msgs.length > 0) {
        const lastMsg = msgs[msgs.length - 1]
        setLastMessages(prev => ({ ...prev, [groupId]: lastMsg }))
      }
    } catch {
      setMessages([])
      prevMsgIdsRef.current = ''
    } finally {
      if (!isPolling) setLoadingMessages(false)
    }
  }, [sessionId, scrollToBottom])

  /* ── Select a group ──────────────────────────────────────────────── */
  const handleSelectGroup = useCallback((group: WAGroup) => {
    setSelectedGroup(group)
    setShowInfo(true)
    // Reset fingerprint so initial load always applies
    prevMsgIdsRef.current = ''
    fetchGroupInfo(group.id)
    fetchMessages(group.id)
    // Clear unread count for this group (shared context)
    markGroupRead(group.id)
    // Update URL to reflect the selected group (use group name as slug)
    const slug = encodeURIComponent(group.name)
    navigate(`/groups/${slug}`, { replace: true })
    // Check if this group is linked to a customer
    setLinkedCustomer(null)
    apiGet<any>('/paid-customers/by-channel', { channel_type: 'wa_live', channel_ref_id: group.id, channel_name: group.name })
      .then(data => { if (data?.customer_id) setLinkedCustomer(data) })
      .catch(() => {})
  }, [fetchGroupInfo, fetchMessages, markGroupRead, navigate])

  /* ── Media gallery helpers ──────────────────────────────────────── */
  const fetchMediaCounts = useCallback(async (groupId: string) => {
    try {
      const res = await apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(groupId)}/media/counts`)
      if (res && typeof res.total === 'number') setMediaCounts(res)
      else setMediaCounts({ image: 0, video: 0, document: 0, total: 0 })
    } catch {
      setMediaCounts({ image: 0, video: 0, document: 0, total: 0 })
    }
  }, [])

  const fetchMediaItems = useCallback(async (groupId: string, type: string = 'image', page: number = 1) => {
    setMediaLoading(true)
    try {
      const res = await apiGet<any>(`/integrations/whatsapp/groups/${encodeURIComponent(groupId)}/media`, { type, page, limit: 50 })
      setMediaItems(res?.items || [])
    } catch {
      setMediaItems([])
    } finally {
      setMediaLoading(false)
    }
  }, [])

  // Fetch media when tab changes or group changes
  useEffect(() => {
    if (!selectedGroup) return
    fetchMediaItems(selectedGroup.id, mediaTab)
  }, [selectedGroup?.id, mediaTab, fetchMediaItems])

  // Fetch media counts when group is selected
  useEffect(() => {
    if (!selectedGroup) return
    fetchMediaCounts(selectedGroup.id)
  }, [selectedGroup?.id, fetchMediaCounts])

  /* ── Auto-select group from URL param ────────────────────────────── */
  useEffect(() => {
    if (!urlGroupId || groups.length === 0 || selectedGroup) return
    const decoded = decodeURIComponent(urlGroupId)
    // Match by group name (slug) or by group ID
    const match = groups.find(g => g.name === decoded || g.id === decoded)
    if (match) {
      handleSelectGroup(match)
    }
  }, [urlGroupId, groups, selectedGroup, handleSelectGroup])

  /* ── Poll for new messages every 5s ───────────────────────────────── */
  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current)
    if (selectedGroup) {
      pollRef.current = setInterval(() => fetchMessages(selectedGroup.id, true), 5000)
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [selectedGroup, fetchMessages])

  /* ── Auto-scroll on initial message load (not polling) ─────────── */
  const initialScrollDone = useRef(false)
  useEffect(() => {
    if (messages.length > 0 && !initialScrollDone.current) {
      initialScrollDone.current = true
      scrollToBottom()
    }
  }, [messages, scrollToBottom])

  // Reset initial scroll flag when switching groups
  useEffect(() => {
    initialScrollDone.current = false
  }, [selectedGroup])

  /* ── Socket: listen for group events ──────────────────────────────── */
  useEffect(() => {
    if (!socket) return
    const handleGroupEvent = (payload: any) => {
      // Refresh group list on join/leave
      if (payload.event === 'group.join' || payload.event === 'group.leave') {
        fetchGroups()
      }
    }
    const handleGroupMessage = (payload: any) => {
      // Track last message for any group (for list preview)
      if (payload.groupId && payload.message) {
        setLastMessages(prev => ({ ...prev, [payload.groupId]: payload.message }))
        // Unread tracking is handled by the shared GroupUnreadContext
      }
      // If we're viewing this group, add the message in real-time
      if (selectedGroup && payload.groupId === selectedGroup.id && payload.message) {
        setMessages(prev => {
          const exists = prev.some(m => m.id === payload.message.id)
          if (exists) return prev
          return [...prev, payload.message]
        })
        // Scroll to show the new real-time message
        setTimeout(() => scrollToBottom(), 50)
      }
    }
    // Live reaction updates — swap the reactions array on the affected message.
    const handleGroupReaction = (payload: any) => {
      if (!selectedGroup || payload.groupId !== selectedGroup.id || !payload.messageId) return
      setMessages(prev => prev.map(m => {
        if (m.id !== payload.messageId) return m
        const meta = typeof m.metadata === 'string' ? (() => { try { return JSON.parse(m.metadata) } catch { return {} } })() : (m.metadata || {})
        return { ...m, metadata: { ...meta, reactions: payload.reactions || [] } }
      }))
    }
    socket.on('whatsapp_group_event', handleGroupEvent)
    socket.on('whatsapp_group_message', handleGroupMessage)
    socket.on('whatsapp_group_reaction', handleGroupReaction)
    return () => {
      socket.off('whatsapp_group_event', handleGroupEvent)
      socket.off('whatsapp_group_message', handleGroupMessage)
      socket.off('whatsapp_group_reaction', handleGroupReaction)
    }
  }, [socket, selectedGroup, fetchGroups])

  /* ── Handle file selection ───────────────────────────────────────── */
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    // Read file as base64
    const reader = new FileReader()
    reader.onload = () => {
      const base64Full = reader.result as string
      const base64Data = base64Full.split(',')[1] || base64Full
      const preview = file.type.startsWith('image/') ? base64Full : undefined
      setPendingFile({
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        preview,
        base64: base64Data,
      })
    }
    reader.readAsDataURL(file)
    // Reset the input so the same file can be selected again
    e.target.value = ''
  }, [])

  /* ── Send message ─────────────────────────────────────────────────── */
  // ── @-mention picker ────────────────────────────────────────────────
  // Detect a trailing "@token" as the user types and open the participant picker.
  const handleComposerChange = (val: string) => {
    setMessageText(val)
    const m = val.match(/@([^\s@]*)$/)
    setMentionQuery(m ? m[1] : null)
  }

  // Lazy-load a participant's WhatsApp profile picture (cached per jid).
  const loadParticipantPic = useCallback((jid: string) => {
    if (!jid || participantPics[jid] !== undefined) return
    setParticipantPics(prev => ({ ...prev, [jid]: '' })) // mark in-flight
    apiGet<{ url: string | null }>(`/integrations/whatsapp/contacts/${encodeURIComponent(jid)}/picture`)
      .then(r => { if (r?.url) setParticipantPics(prev => ({ ...prev, [jid]: r.url as string })) })
      .catch(() => {})
  }, [participantPics])

  // Insert the chosen participant as "@Name " and remember the mention.
  const insertMention = (p: { id: string; number?: string; name?: string }) => {
    const name = (p.name || p.number || (p.id || '').split('@')[0] || 'member').trim()
    const number = p.number || (p.id || '').split('@')[0]
    setMessageText(prev => prev.replace(/@([^\s@]*)$/, `@${name} `))
    setMentions(prev => [...prev, { jid: p.id, number, name }])
    setMentionQuery(null)
  }

  const filteredParticipants = (groupInfo?.participants || []).filter(p => {
    if (mentionQuery == null) return false
    const q = mentionQuery.toLowerCase()
    return !q || (p.name || '').toLowerCase().includes(q) || (p.number || '').includes(q)
  }).slice(0, 8)

  const handleSend = async () => {
    if (!selectedGroup || !sessionId) return
    if (!messageText.trim() && !pendingFile) return
    try {
      setSendingMessage(true)
      if (pendingFile) {
        // Send file with optional caption
        let sendType = 'document'
        if (pendingFile.type.startsWith('image/')) sendType = 'image'
        else if (pendingFile.type.startsWith('video/')) sendType = 'video'

        await apiPost(`/integrations/whatsapp/groups/${encodeURIComponent(selectedGroup.id)}/send`, {
          sessionId,
          type: sendType,
          base64: pendingFile.base64,
          mimetype: pendingFile.type,
          filename: pendingFile.name,
          caption: messageText.trim() || undefined,
          replyToId: replyTo?.id || undefined,
        })
        setPendingFile(null)
      } else {
        // Send text message. Transform "@Name" tokens into "@<number>" (what
        // WhatsApp expects) and pass the matching JIDs as `mentions` so the
        // recipients see a real highlighted mention.
        let outText = messageText.trim()
        const usedMentions: string[] = []
        for (const mn of mentions) {
          const token = `@${mn.name}`
          if (outText.includes(token)) {
            outText = outText.split(token).join(`@${mn.number}`)
            if (!usedMentions.includes(mn.jid)) usedMentions.push(mn.jid)
          }
        }
        await apiPost(`/integrations/whatsapp/groups/${encodeURIComponent(selectedGroup.id)}/send`, {
          sessionId,
          text: outText,
          mentions: usedMentions.length ? usedMentions : undefined,
          replyToId: replyTo?.id || undefined,
        })
      }
      setMessageText('')
      setMentions([])
      setMentionQuery(null)
      setReplyTo(null)
      // Refresh messages after sending, then scroll to see the new message
      // Use isPolling=true to avoid showing loading spinner (which unmounts the message list and resets scroll)
      setTimeout(async () => {
        await fetchMessages(selectedGroup.id, true)
        setTimeout(() => scrollToBottom(), 100)
      }, 1000)
    } catch (err: any) {
      console.error('Failed to send group message:', err)
    } finally {
      setSendingMessage(false)
    }
  }

  /* ── Edit message ─────────────────────────────────────────────────── */
  const handleEditStart = (msg: GroupMessage) => {
    setEditingMessage(msg)
    setEditText(msg.body || '')
    setContextMenu(null)
    setTimeout(() => editInputRef.current?.focus(), 50)
  }

  const handleEditSave = async () => {
    if (!editingMessage || !sessionId || !editText.trim()) return
    try {
      await apiPost('/integrations/whatsapp/messages/edit', {
        sessionId,
        chatId: editingMessage.chatId,
        messageId: editingMessage.id,
        newContent: editText.trim(),
      })
      // Update message locally
      setMessages(prev => prev.map(m =>
        m.id === editingMessage.id ? { ...m, body: editText.trim() } : m
      ))
      setEditingMessage(null)
      setEditText('')
    } catch (err: any) {
      console.error('Failed to edit message:', err)
      alert('Failed to edit message. Only recent outgoing text messages can be edited.')
    }
  }

  const handleEditCancel = () => {
    setEditingMessage(null)
    setEditText('')
  }

  /* ── Context menu for messages ───────────────────────────────────── */
  const handleMessageContextMenu = (e: React.MouseEvent, msg: GroupMessage) => {
    e.preventDefault()
    // Position menu, but keep it on screen
    const x = Math.min(e.clientX, window.innerWidth - 200)
    const y = Math.min(e.clientY, window.innerHeight - 300)
    setContextMenu({ x, y, msg })
  }

  // Star / unstar a message
  const handleToggleStar = async (msg: GroupMessage) => {
    if (!sessionId || !selectedGroup) return
    try {
      const res = await apiPost<{ starred: boolean }>(`/integrations/whatsapp/groups/${encodeURIComponent(selectedGroup.id)}/messages/${encodeURIComponent(msg.id)}/star`, {})
      setMessages(prev => prev.map(m => m.id === msg.id ? { ...m, isStarred: res.starred } : m))
    } catch { /* fail silently */ }
    setContextMenu(null)
  }

  // Enter multi-select delete mode (WhatsApp-style)
  const handleDeleteMessage = (msg: GroupMessage) => {
    setDeleteMode(true)
    setSelectedMsgIds(new Set([msg.id]))
    setContextMenu(null)
  }

  // Toggle message selection in delete mode
  const toggleMsgSelection = (msgId: string) => {
    setSelectedMsgIds(prev => {
      const next = new Set(prev)
      if (next.has(msgId)) next.delete(msgId)
      else next.add(msgId)
      return next
    })
  }

  // Cancel delete mode
  const cancelDeleteMode = () => {
    setDeleteMode(false)
    setSelectedMsgIds(new Set())
  }

  // Execute multi-delete: delete from OpenWA + local DB
  const executeMultiDelete = async () => {
    if (!sessionId || !selectedGroup || selectedMsgIds.size === 0 || isDeleting) return
    setIsDeleting(true)
    const ids = Array.from(selectedMsgIds)
    try {
      // Delete each message via OpenWA API (real WhatsApp deletion) + local DB
      await Promise.allSettled(ids.map(async (msgId) => {
        // Try OpenWA delete first (for everyone)
        try {
          await apiPost('/integrations/whatsapp/messages/delete', {
            sessionId, chatId: selectedGroup.id, messageId: msgId, forEveryone: true,
          })
        } catch { /* OpenWA may fail for old msgs — continue */ }
        // Always delete from local DB
        try {
          await apiDelete(`/integrations/whatsapp/groups/${encodeURIComponent(selectedGroup.id)}/messages/${encodeURIComponent(msgId)}`)
        } catch { /* fail silently */ }
      }))
      setMessages(prev => prev.filter(m => !selectedMsgIds.has(m.id)))
    } catch { /* fail silently */ }
    setIsDeleting(false)
    setDeleteMode(false)
    setSelectedMsgIds(new Set())
  }

  // Reply to a message
  const handleReply = (msg: GroupMessage) => {
    setReplyTo(msg)
    setContextMenu(null)
  }

  // Forward a message
  const handleForward = (msg: GroupMessage) => {
    setForwardMsg(msg)
    setForwardSearch('')
    setContextMenu(null)
  }

  // Execute forward to the selected group
  const executeForward = async () => {
    if (!forwardMsg || !sessionId || !forwardTarget || forwardSending) return
    const text = forwardMsg.body || ''
    if (!text) return
    setForwardSending(true)
    try {
      await apiPost(`/integrations/whatsapp/groups/${encodeURIComponent(forwardTarget)}/send`, {
        sessionId, text, type: 'text',
      })
      setForwardSent(true)
      setTimeout(() => {
        setForwardMsg(null)
        setForwardTarget(null)
        setForwardSending(false)
        setForwardSent(false)
        setForwardSearch('')
      }, 1200)
    } catch {
      setForwardSending(false)
    }
  }

  // Reset forward state when modal closes
  const closeForwardModal = () => {
    setForwardMsg(null)
    setForwardTarget(null)
    setForwardSending(false)
    setForwardSent(false)
    setForwardSearch('')
  }

  // Close context menu on click elsewhere
  useEffect(() => {
    const close = () => setContextMenu(null)
    if (contextMenu) window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [contextMenu])

  /* ── Filter groups (by search + tags) ─────────────────────────────── */
  const filteredGroups = groups.filter(g => {
    // Text search filter
    if (searchQuery && !g.name.toLowerCase().includes(searchQuery.toLowerCase())) return false
    // Tag filter: if any tag filters are active, group must have at least one of the selected tags
    if (activeTagFilters.length > 0) {
      const groupTags = groupTagMap[g.id] || []
      const groupTagIds = groupTags.map(t => t.tag_id)
      if (!activeTagFilters.some(filterId => groupTagIds.includes(filterId))) return false
    }
    return true
  }).sort((a, b) => {
    // WhatsApp-style: most-recently-active group floats to the top.
    const ta = lastMessages[a.id]?.timestamp ?? 0
    const tb = lastMessages[b.id]?.timestamp ?? 0
    if (tb !== ta) return tb - ta
    return a.name.localeCompare(b.name) // stable fallback for groups with no messages yet
  })

  /* ── Format timestamp ─────────────────────────────────────────────── */
  const formatTime = (ts?: number) => {
    if (!ts) return ''
    const d = new Date(ts * 1000)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = (ts?: number) => {
    if (!ts) return ''
    const d = new Date(ts * 1000)
    const today = new Date()
    if (d.toDateString() === today.toDateString()) return 'Today'
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)
    if (d.toDateString() === yesterday.toDateString()) return 'Yesterday'
    return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' })
  }

  /* ── Extract sender display name from message ─────────────────────── */
  const getSenderName = (msg: GroupMessage) => {
    if (msg.direction === 'OUTGOING') return 'You'
    // Use senderName if available (stored from webhook pushName)
    if (msg.senderName && msg.senderName !== 'Unknown' && !msg.senderName.includes('@g.us')) return msg.senderName
    // Try to get name from the 'from' field (phone number or contact)
    const from = msg.from || ''
    const phone = from.replace(/@c\.us$|@s\.whatsapp\.net$/, '')
    // Look up in group participants
    if (groupInfo?.participants) {
      const participant = groupInfo.participants.find(p => p.id === from || p.number === phone)
      if (participant?.name) return participant.name
    }
    // Fallback: look up in contacts map
    if (contactsMap[from]) return contactsMap[from]
    if (contactsMap[`${phone}@c.us`]) return contactsMap[`${phone}@c.us`]
    return phone || 'Unknown'
  }

  /* ── Sender color for messages ────────────────────────────────────── */
  // WhatsApp-style sender name colors
  const senderColors = [
    'text-[#e06c75]', 'text-[#06cf9c]', 'text-[#53bdeb]', 'text-[#e9a032]',
    'text-[#7c6bff]', 'text-[#f06292]', 'text-[#26c6da]', 'text-[#ff7043]',
  ]
  const getSenderColor = (senderId: string) => {
    let hash = 0
    for (let i = 0; i < senderId.length; i++) hash = senderId.charCodeAt(i) + ((hash << 5) - hash)
    return senderColors[Math.abs(hash) % senderColors.length]
  }

  /* ── Linkify: turn URLs in message text into clickable links ──────── */
  const linkifyText = (text: string): React.ReactNode => {
    // Match URLs (http/https/www) and common patterns
    const urlRegex = /(https?:\/\/[^\s<>]+|www\.[^\s<>]+)/gi
    const parts = text.split(urlRegex)
    if (parts.length === 1) return text // No URLs found

    return parts.map((part, i) => {
      if (urlRegex.test(part) || /^https?:\/\//i.test(part) || /^www\./i.test(part)) {
        // Reset regex lastIndex since we're reusing it
        urlRegex.lastIndex = 0
        const href = part.startsWith('www.') ? `https://${part}` : part
        // Clean trailing punctuation that's likely not part of the URL
        const cleanHref = href.replace(/[)}\].,;:!?]+$/, '')
        const cleanText = part.replace(/[)}\].,;:!?]+$/, '')
        const trailing = part.slice(cleanText.length)
        return (
          <React.Fragment key={i}>
            <a
              href={cleanHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 underline hover:underline break-all"
              onClick={(e) => e.stopPropagation()}
            >
              {cleanText}
            </a>
            {trailing}
          </React.Fragment>
        )
      }
      return <React.Fragment key={i}>{part}</React.Fragment>
    })
  }

  /* ── Last message preview for group list ──────────────────────────── */
  const getMessagePreview = (groupId: string): { sender: string; text: string; time: string } | null => {
    const msg = lastMessages[groupId]
    if (!msg) return null
    const sender = msg.direction === 'OUTGOING' ? 'You' : (msg.senderName && msg.senderName !== 'Unknown' ? msg.senderName.split(' ')[0] : '')
    let text = msg.body || ''
    if (!text) {
      if (msg.type === 'image') text = '📷 Photo'
      else if (msg.type === 'video') text = '🎥 Video'
      else if (msg.type === 'audio' || msg.type === 'ptt') text = '🎵 Audio'
      else if (msg.type === 'document') text = '📄 Document'
      else if (msg.type === 'sticker') text = '🏷 Sticker'
      else if (msg.mediaUrl) text = '📎 Attachment'
      else text = `[${msg.type}]`
    }
    const time = msg.timestamp ? formatTime(msg.timestamp) : ''
    return { sender, text, time }
  }

  /* ═══════════════════════════════════════════════════════════════════ */
  /*  RENDER                                                            */
  /* ═══════════════════════════════════════════════════════════════════ */

  return (
    <div className="h-full flex overflow-hidden">
      {/* ── Left panel: Group list ───────────────────────────────────── */}
      <div className="w-[420px] flex flex-col border-r border-border bg-surface shrink-0">
        {/* Header */}
        <div className="px-6 pt-6 pb-4 border-b border-border">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-txt-primary">Groups</h1>
                {Object.values(unreadGroups).reduce((a, b) => a + b, 0) > 0 && (
                  <span className="min-w-[22px] h-[22px] flex items-center justify-center rounded-full bg-green-500 text-white text-xs font-bold px-1.5">
                    {Object.values(unreadGroups).reduce((a, b) => a + b, 0)}
                  </span>
                )}
              </div>
              <p className="text-sm text-txt-secondary mt-0.5">
                {groups.length} WhatsApp group{groups.length !== 1 ? 's' : ''}
              </p>
              {/* Renders only when WA is disconnected — sourced from global status context */}
              <CachedDataNote className="mt-2" />
            </div>
            <button
              onClick={() => fetchGroups()}
              className="p-2 rounded-lg text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border border-border transition-colors"
              title="Refresh groups"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search groups..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary placeholder-txt-tertiary outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors"
            />
          </div>

          {/* Tag filter chips */}
          {allTags.length > 0 && (
            <div className="flex items-center gap-1.5 mt-3 flex-wrap">
              {allTags.map(tag => {
                const isActive = activeTagFilters.includes(tag.id)
                return (
                  <button
                    key={tag.id}
                    onClick={() => setActiveTagFilters(prev =>
                      isActive ? prev.filter(id => id !== tag.id) : [...prev, tag.id]
                    )}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium transition-all cursor-pointer"
                    style={{
                      backgroundColor: isActive ? tag.color : 'transparent',
                      color: isActive ? '#fff' : tag.color,
                      border: `1.5px solid ${tag.color}`,
                      opacity: isActive ? 1 : 0.7,
                    }}
                    title={isActive ? `Remove ${tag.name} filter` : `Filter by ${tag.name}`}
                  >
                    {tag.name}
                    {isActive && (
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                  </button>
                )
              })}
              <button
                onClick={() => setShowTagManager(true)}
                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[11px] text-txt-tertiary hover:text-txt-secondary hover:bg-surface-hover transition-colors"
                title="Manage tags"
              >
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />
                </svg>
              </button>
              {activeTagFilters.length > 0 && (
                <button
                  onClick={() => setActiveTagFilters([])}
                  className="text-[11px] text-txt-tertiary hover:text-error transition-colors ml-1"
                >
                  Clear
                </button>
              )}
            </div>
          )}
        </div>

        {/* Group list */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="mx-4 mt-4 px-4 py-3 rounded-lg bg-error-light border border-error/20 text-error text-sm">
              {error}
            </div>
          )}

          {loading && groups.length === 0 ? (
            <div className="flex items-center justify-center h-48">
              <div className="flex flex-col items-center gap-3">
                <svg className="w-8 h-8 text-brand-500 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span className="text-sm text-txt-secondary">Loading groups...</span>
              </div>
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-center px-6">
              <GroupIcon className="w-12 h-12 text-txt-tertiary mb-3" />
              <p className="text-sm font-medium text-txt-secondary">No groups found</p>
              <p className="text-xs text-txt-tertiary mt-1">
                {searchQuery ? 'Try a different search term' : 'No WhatsApp groups available'}
              </p>
            </div>
          ) : (
            filteredGroups.map((group) => {
              const preview = getMessagePreview(group.id)
              const unreadCount = unreadGroups[group.id] || 0
              const hasActiveNotice = activeNotices.some(n => n.group_ids.length === 0 || n.group_ids.includes(group.id))
              return (
                <div
                  key={group.id}
                  onClick={() => handleSelectGroup(group)}
                  className={`px-4 py-3 border-b border-border cursor-pointer transition-all duration-100 ${
                    selectedGroup?.id === group.id
                      ? 'bg-green-50/60 dark:bg-green-900/20 border-l-2 border-l-green-600'
                      : 'hover:bg-surface-hover border-l-2 border-l-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <GroupAvatar pictureUrl={groupPictures[group.id]} groupName={group.name} size="md">
                      {unreadCount > 0 ? (
                        <span className="absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-white dark:border-gray-800 z-10" title={`${unreadCount} unread`} />
                      ) : linkedMap[group.id] ? (
                        // Linked-customer indicator — small brand-600 badge in the
                        // bottom-right corner of the avatar. Unread takes priority
                        // when there's actual activity; otherwise we surface the
                        // link so agents can tell at a glance which groups map to
                        // a paid customer.
                        <span
                          className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-brand-600 text-white border-2 border-white dark:border-gray-800 z-10 flex items-center justify-center"
                          title={`Linked to ${linkedMap[group.id].customer_name}${linkedMap[group.id].wequote_id ? ` (#${linkedMap[group.id].wequote_id})` : ''}`}
                          aria-label={`Linked to ${linkedMap[group.id].customer_name}`}
                        >
                          <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V4zm4 3h4a1 1 0 010 2H8a1 1 0 010-2zm0 3h4a1 1 0 010 2H8a1 1 0 010-2z" clipRule="evenodd" />
                          </svg>
                        </span>
                      ) : null}
                    </GroupAvatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <h4 className={`text-sm truncate ${unreadCount > 0 ? 'font-bold text-txt-primary' : 'font-semibold text-txt-primary'}`}>{group.name}</h4>
                          {hasActiveNotice && (
                            <span className="shrink-0" title="Auto-reply active">
                              <svg className="w-3.5 h-3.5 text-amber-500" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M13 10V3L4 14h7v7l9-11h-7z"/>
                              </svg>
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {preview?.time && (
                            <span className={`text-[11px] ${unreadCount > 0 ? 'text-green-500 font-semibold' : 'text-txt-tertiary'}`}>{preview.time}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <div className="flex items-center gap-2 min-w-0">
                          {group.participantsCount && (
                            <span className="text-xs text-txt-tertiary flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                              </svg>
                              {group.participantsCount}
                            </span>
                          )}
                          {group.isAdmin && (
                            <Badge variant="plan" value="Admin" />
                          )}
                          {/* Tag chips */}
                          {(groupTagMap[group.id] || []).map(tag => (
                            <span
                              key={tag.tag_id}
                              className="inline-flex items-center px-1.5 py-px rounded-full text-[10px] font-semibold shrink-0"
                              style={{ backgroundColor: tag.color + '20', color: tag.color, border: `1px solid ${tag.color}40` }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                        {unreadCount > 0 && (
                          <span className="shrink-0 min-w-[20px] h-5 flex items-center justify-center rounded-full bg-green-500 text-white text-[11px] font-bold px-1.5">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        )}
                      </div>
                      {preview && (
                        <p className={`text-xs truncate mt-1 ${unreadCount > 0 ? 'text-txt-primary font-medium' : 'text-txt-tertiary'}`} style={{ lineHeight: '1.4' }}>
                          {preview.sender && <span className={unreadCount > 0 ? 'text-txt-primary font-semibold' : 'text-txt-secondary font-medium'}>{preview.sender}: </span>}
                          {preview.text}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* ── Right panel: Group chat / placeholder ────────────────────── */}
      <div className="flex-1 flex flex-col bg-surface overflow-hidden">
        {!selectedGroup ? (
          /* No group selected placeholder */
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-20 h-20 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mb-4">
              <GroupIcon className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-lg font-semibold text-txt-primary mb-2">WhatsApp Groups</h2>
            <p className="text-sm text-txt-secondary max-w-md">
              Select a group from the left to view messages and send replies. All agents can see and participate in group conversations.
            </p>
          </div>
        ) : (
          <>
            {/* ── Group header ──────────────────────────────────────── */}
            <div className="flex-shrink-0 flex items-center justify-between px-5 py-2.5" style={{ backgroundColor: '#f0f2f5', borderBottom: '1px solid #e9edef' }}>
              <div className="flex items-center gap-3 min-w-0">
                <GroupAvatar pictureUrl={groupPictures[selectedGroup.id]} groupName={selectedGroup.name} size="sm" />
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-txt-primary truncate">{selectedGroup.name}</h2>
                  <p className="text-xs text-txt-secondary truncate">
                    {groupInfo?.participants?.length || selectedGroup.participantsCount || 0} members
                    {groupInfo?.description ? ` · ${groupInfo.description.substring(0, 60)}${groupInfo.description.length > 60 ? '...' : ''}` : ''}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {linkedCustomer && (
                  <button
                    onClick={() => navigate(`/customers/${linkedCustomer.customer_id}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-300 transition-colors text-xs font-medium"
                    title={`View customer: ${linkedCustomer.company_name}`}
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 7.5h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                    </svg>
                    {linkedCustomer.company_name}
                  </button>
                )}
                <button
                  onClick={() => setShowInfo(!showInfo)}
                  className={`p-2 rounded-lg border transition-colors ${
                    showInfo
                      ? 'text-brand-600 bg-brand-50 border-brand-200'
                      : 'text-txt-secondary hover:text-txt-primary hover:bg-surface-hover border-border'
                  }`}
                  title="Group info"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
                  </svg>
                </button>
              </div>
            </div>

            {/* ── Active auto-reply notice banner ──────────────────── */}
            {activeNotices.length > 0 && (() => {
              // Filter notices that apply to this group
              const relevantNotices = activeNotices.filter(n =>
                n.group_ids.length === 0 || n.group_ids.includes(selectedGroup.id)
              )
              if (relevantNotices.length === 0) return null
              return (
                <div className="flex-shrink-0 flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
                  <svg className="w-4 h-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                  </svg>
                  <span className="text-xs text-amber-700 dark:text-amber-300">
                    <span className="font-semibold">Auto-reply active:</span>{' '}
                    {relevantNotices.map(n => n.name).join(', ')}
                    {relevantNotices[0].schedule_type === 'scheduled' && relevantNotices[0].end_date && (
                      <span className="text-amber-600 dark:text-amber-400"> (until {new Date(relevantNotices[0].end_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })})</span>
                    )}
                  </span>
                </div>
              )
            })()}

            {/* ── Content area (messages + optional info panel) ─────── */}
            <div className="flex-1 flex overflow-hidden">
              {/* Messages */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Message list */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-1" style={{ backgroundColor: '#efeae2', backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'200\' height=\'200\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cdefs%3E%3Cpattern id=\'p\' width=\'40\' height=\'40\' patternUnits=\'userSpaceOnUse\'%3E%3Cpath d=\'M20 0 L20 40 M0 20 L40 20\' stroke=\'%23d6d0c6\' stroke-width=\'0.4\' fill=\'none\' opacity=\'0.3\'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width=\'200\' height=\'200\' fill=\'url(%23p)\'/%3E%3C/svg%3E")' }}>
                  {loadingMessages ? (
                    <div className="flex items-center justify-center h-full">
                      <div className="flex flex-col items-center gap-3">
                        <svg className="w-8 h-8 text-green-500 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        <span className="text-sm text-txt-secondary">Loading messages...</span>
                      </div>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-sm text-txt-tertiary">No messages yet</p>
                    </div>
                  ) : (
                    <>
                      {messages.map((msg, i) => {
                        const isOutgoing = msg.direction === 'OUTGOING'
                        const senderName = getSenderName(msg)
                        const prevMsg = i > 0 ? messages[i - 1] : null
                        const showDate = !prevMsg || formatDate(msg.timestamp) !== formatDate(prevMsg.timestamp)
                        const showSender = !isOutgoing && (!prevMsg || prevMsg.from !== msg.from || showDate)

                        return (
                          <React.Fragment key={msg.id}>
                            {showDate && (
                              <div className="flex items-center justify-center my-3">
                                <span className="px-3 py-1.5 rounded-lg text-xs font-medium shadow-sm" style={{ backgroundColor: '#d1f4cc', color: '#1b4332' }}>
                                  {formatDate(msg.timestamp)}
                                </span>
                              </div>
                            )}
                            <div
                              className={`flex items-start gap-2 ${isOutgoing && !deleteMode ? 'justify-end' : ''}`}
                              onClick={deleteMode ? () => toggleMsgSelection(msg.id) : undefined}
                            >
                              {/* Delete mode checkbox */}
                              {deleteMode && (
                                <div className="flex-shrink-0 pt-2 pl-1">
                                  <div
                                    className="w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer transition-colors"
                                    style={{
                                      borderColor: selectedMsgIds.has(msg.id) ? '#22c55e' : '#9ca3af',
                                      backgroundColor: selectedMsgIds.has(msg.id) ? '#22c55e' : 'transparent',
                                    }}
                                  >
                                    {selectedMsgIds.has(msg.id) && (
                                      <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                    )}
                                  </div>
                                </div>
                              )}
                              <div
                                className="relative max-w-[75%]"
                                onMouseEnter={() => setHoveredMsgId(msg.id)}
                                onMouseLeave={() => setHoveredMsgId(null)}
                              >
                                {/* The bubble container */}
                                <div className="relative">
                                {/* WhatsApp-style tail */}
                                {(showSender || (isOutgoing && (!prevMsg || prevMsg.direction !== 'OUTGOING' || showDate))) && (
                                  <div
                                    className="absolute top-0 w-3 h-3"
                                    style={{
                                      ...(isOutgoing ? { right: '-6px' } : { left: '-6px' }),
                                      background: isOutgoing ? '#d9fdd3' : '#ffffff',
                                      clipPath: isOutgoing ? 'polygon(0 0, 100% 0, 0 100%)' : 'polygon(0 0, 100% 0, 100% 100%)',
                                    }}
                                  />
                                )}
                                <div
                                  id={`msg-${msg.id}`}
                                  className="relative rounded-lg px-2.5 py-1.5 shadow-sm group/msg transition-all duration-300"
                                  style={{
                                    backgroundColor: isOutgoing ? '#d9fdd3' : '#ffffff',
                                    borderTopLeftRadius: !isOutgoing && showSender ? '0' : undefined,
                                    borderTopRightRadius: isOutgoing && (!prevMsg || prevMsg.direction !== 'OUTGOING' || showDate) ? '0' : undefined,
                                  }}
                                  onContextMenu={(e) => handleMessageContextMenu(e, msg)}
                                >
                                  {/* Hover dropdown chevron — inside bubble, top-right */}
                                  {!editingMessage && !deleteMode && hoveredMsgId === msg.id && (
                                    <button
                                      className="z-20 rounded-full p-0.5 transition-colors"
                                      style={{ position: 'absolute', top: '4px', right: '4px', backgroundColor: isOutgoing ? '#d9fdd3' : '#ffffff' }}
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
                                        setContextMenu({ x: rect.right + 4, y: rect.top, msg })
                                      }}
                                      title="Message actions"
                                    >
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="#8696a0" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                      </svg>
                                    </button>
                                  )}
                                  {/* Star indicator */}
                                  {msg.isStarred && (
                                    <span className="absolute -top-1.5 -left-1.5 text-amber-400 z-10" title="Starred">
                                      <svg className="w-3.5 h-3.5 fill-current" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                                    </span>
                                  )}
                                  {/* Quoted reply — uses DB join data or metadata fallback */}
                                  {(() => {
                                    const meta = typeof msg.metadata === 'string' ? (() => { try { return JSON.parse(msg.metadata) } catch { return {} } })() : (msg.metadata || {})
                                    const qBody = msg.replyBody || meta.replyBody || null
                                    const qSender = msg.replySenderName || meta.replySenderName || null
                                    const qType = msg.replyType || meta.replyType || null
                                    if (!msg.replyToId || !qBody) return null
                                    return (
                                      <div className="rounded-md px-2.5 py-1.5 mb-1 border-l-4 border-green-500 cursor-pointer" style={{ backgroundColor: isOutgoing ? '#c8edd0' : '#f0f0f0' }}
                                        onClick={() => {
                                          const el = document.getElementById(`msg-${msg.replyToId}`)
                                          if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.classList.add('ring-2', 'ring-green-400'); setTimeout(() => el.classList.remove('ring-2', 'ring-green-400'), 2000) }
                                        }}
                                      >
                                        <p className="text-[11px] font-semibold text-green-700">{qSender || 'Unknown'}</p>
                                        <p className="text-[12px] text-gray-600 truncate">{qType && qType !== 'chat' && qType !== 'text' ? `[${qType}]` : qBody}</p>
                                      </div>
                                    )
                                  })()}
                                  {showSender && (
                                    <p className={`text-[13px] font-semibold mb-0.5 ${getSenderColor(msg.from)}`} style={{ lineHeight: '1.3' }}>
                                      {senderName}
                                    </p>
                                  )}
                                  {/* Media display */}
                                  {msg.mediaUrl && (msg.type === 'image' || msg.mediaUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)) && (
                                    <img
                                      src={msg.mediaUrl}
                                      alt={msg.body || 'Image'}
                                      className="max-w-full rounded-md mb-1 cursor-pointer hover:opacity-90 transition-opacity"
                                      style={{ maxHeight: '300px' }}
                                      onClick={() => window.open(msg.mediaUrl, '_blank')}
                                      onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                    />
                                  )}
                                  {msg.mediaUrl && msg.type === 'video' && (
                                    <video
                                      src={msg.mediaUrl}
                                      controls
                                      className="max-w-full rounded-md mb-1"
                                      style={{ maxHeight: '300px' }}
                                    />
                                  )}
                                  {msg.mediaUrl && (['audio', 'ptt'].includes(msg.type) || msg.mediaUrl.match(/\.(ogg|opus|oga|mp3|m4a|aac|wav)$/i)) && (
                                    <audio
                                      src={msg.mediaUrl}
                                      controls
                                      preload="metadata"
                                      className="mb-1"
                                      style={{ maxWidth: '260px', height: '40px' }}
                                    />
                                  )}
                                  {msg.mediaUrl && !['image', 'video', 'audio', 'ptt'].includes(msg.type) && !msg.mediaUrl.match(/\.(jpg|jpeg|png|gif|webp|ogg|opus|oga|mp3|m4a|aac|wav)$/i) && (() => {
                                    const meta = typeof msg.metadata === 'string' ? (() => { try { return JSON.parse(msg.metadata) } catch { return {} } })() : (msg.metadata || {})
                                    const docName = meta.docFilename || msg.body || msg.mediaUrl?.split('/').pop() || 'Document'
                                    const ext = docName.split('.').pop()?.toLowerCase() || ''
                                    const docMime = meta.docMimetype || ''
                                    const docSize = meta.docFilesize ? (meta.docFilesize > 1024 * 1024 ? `${(meta.docFilesize / (1024 * 1024)).toFixed(1)} MB` : `${Math.round(meta.docFilesize / 1024)} KB`) : null
                                    const isPdf = ext === 'pdf' || docMime.includes('pdf')
                                    const isDoc = ['doc', 'docx'].includes(ext) || docMime.includes('word') || docMime.includes('document')
                                    const isXls = ['xls', 'xlsx', 'csv'].includes(ext) || docMime.includes('sheet') || docMime.includes('excel')
                                    const typeLabel = isPdf ? 'PDF' : isDoc ? 'DOCX' : isXls ? 'XLS' : ext.toUpperCase() || 'FILE'
                                    const iconColor = isPdf ? '#E53935' : isDoc ? '#1565C0' : isXls ? '#2E7D32' : '#546E7A'
                                    const bgColor = isOutgoing ? 'rgba(0,0,0,0.06)' : 'rgba(0,0,0,0.04)'
                                    return (
                                      <a href={msg.mediaUrl} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-3 rounded-lg mb-1 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
                                        style={{ backgroundColor: bgColor, minWidth: '240px', maxWidth: '320px', textDecoration: 'none' }}
                                      >
                                        {/* File type icon */}
                                        <div className="flex items-center justify-center shrink-0" style={{ width: '56px', height: '56px', backgroundColor: iconColor }}>
                                          {isPdf ? (
                                            <svg className="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8.5 7.5c0 .83-.67 1.5-1.5 1.5H9v2H7.5V7H10c.83 0 1.5.67 1.5 1.5v1zm5 2c0 .83-.67 1.5-1.5 1.5h-2.5V7H15c.83 0 1.5.67 1.5 1.5v3zm4-3H19v1h1.5V11H19v2h-1.5V7h3v1.5zM9 9.5h1v-1H9v1zM4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm10 5.5h1v-3h-1v3z"/></svg>
                                          ) : (
                                            <svg className="w-7 h-7 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" /></svg>
                                          )}
                                        </div>
                                        {/* File details */}
                                        <div className="flex-1 min-w-0 py-2 pr-3">
                                          <p className="text-[13px] font-medium truncate" style={{ color: '#111b21' }}>{docName}</p>
                                          <p className="text-[11px] mt-0.5" style={{ color: '#667781' }}>
                                            {[docSize, typeLabel].filter(Boolean).join(' \u00B7 ')}
                                          </p>
                                        </div>
                                      </a>
                                    )
                                  })()}
                                  {/* Text body (or edit mode) */}
                                  {editingMessage?.id === msg.id ? (
                                    <div className="min-w-[200px]">
                                      <textarea
                                        ref={editInputRef}
                                        value={editText}
                                        onChange={(e) => setEditText(e.target.value)}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleEditSave() }
                                          if (e.key === 'Escape') handleEditCancel()
                                        }}
                                        className="w-full text-[14.2px] bg-white/80 rounded px-2 py-1 border border-blue-300 focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                                        style={{ color: '#111b21', lineHeight: '1.35', minHeight: '36px' }}
                                        rows={Math.max(1, editText.split('\n').length)}
                                      />
                                      <div className="flex items-center gap-1.5 mt-1">
                                        <button onClick={handleEditSave} className="text-[11px] px-2 py-0.5 rounded bg-blue-500 text-white hover:bg-blue-600 transition-colors">
                                          Save
                                        </button>
                                        <button onClick={handleEditCancel} className="text-[11px] px-2 py-0.5 rounded bg-gray-200 text-gray-600 hover:bg-gray-300 transition-colors">
                                          Cancel
                                        </button>
                                        <span className="text-[10px] text-gray-400 ml-1">Enter to save, Esc to cancel</span>
                                      </div>
                                    </div>
                                  ) : msg.body ? (
                                    <p className="text-[14.2px] whitespace-pre-wrap break-words" style={{ color: '#111b21', lineHeight: '1.35' }}>
                                      {linkifyText(msg.body)}
                                    </p>
                                  ) : null}
                                  {!msg.body && !msg.mediaUrl && msg.type !== 'chat' && (
                                    <p className="text-sm italic" style={{ color: '#8696a0' }}>[{msg.type}]</p>
                                  )}
                                  <p className="text-[11px] mt-0.5 text-right" style={{ color: isOutgoing ? '#1b7e4e' : '#667781' }}>
                                    {formatTime(msg.timestamp)}
                                    {isOutgoing && (
                                      <span className="ml-1 inline-block" style={{ color: '#53bdeb' }}>&#10003;&#10003;</span>
                                    )}
                                  </p>
                                  {/* Reactions — stored in metadata.reactions by the webhook */}
                                  {(() => {
                                    const rMeta = typeof msg.metadata === 'string' ? (() => { try { return JSON.parse(msg.metadata) } catch { return {} } })() : (msg.metadata || {})
                                    const rx: { who: string; emoji: string }[] = Array.isArray(rMeta.reactions) ? rMeta.reactions : []
                                    if (!rx.length) return null
                                    const grouped: Record<string, string[]> = {}
                                    rx.forEach((r) => { (grouped[r.emoji] = grouped[r.emoji] || []).push(r.who) })
                                    return (
                                      <div className={`flex gap-1 mt-1 ${isOutgoing ? 'justify-end' : ''}`}>
                                        {Object.entries(grouped).map(([e, whos]) => (
                                          <span key={e} className="px-1.5 py-px rounded-full text-[11px] shadow-sm border" style={{ backgroundColor: '#f7f8fa', borderColor: '#e5e7eb', color: '#54656f' }} title={whos.join(', ')}>
                                            {e}{whos.length > 1 ? ` ${whos.length}` : ''}
                                          </span>
                                        ))}
                                      </div>
                                    )
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                          </React.Fragment>
                        )
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </div>

                {/* Delete mode bottom action bar */}
                {deleteMode && (
                  <div
                    className="flex items-center justify-between px-4 py-2.5 border-t flex-shrink-0"
                    style={{ backgroundColor: '#f0f2f5' }}
                  >
                    <div className="flex items-center gap-3">
                      <button
                        onClick={cancelDeleteMode}
                        className="text-sm font-medium px-3 py-1.5 rounded-lg hover:bg-gray-200 transition-colors"
                        style={{ color: '#667781' }}
                      >
                        Cancel
                      </button>
                      <span className="text-sm font-medium" style={{ color: '#111b21' }}>
                        {selectedMsgIds.size} selected
                      </span>
                    </div>
                    <button
                      onClick={executeMultiDelete}
                      disabled={selectedMsgIds.size === 0 || isDeleting}
                      className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: selectedMsgIds.size > 0 && !isDeleting ? '#ef4444' : '#e5e7eb',
                        color: selectedMsgIds.size > 0 && !isDeleting ? '#ffffff' : '#9ca3af',
                        cursor: selectedMsgIds.size === 0 || isDeleting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                      {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                  </div>
                )}

                {/* Context menu */}
                {contextMenu && (
                  <div
                    className="fixed z-50 bg-white rounded-xl shadow-2xl border border-gray-200 py-1.5 min-w-[180px]"
                    style={{ top: contextMenu.y, left: contextMenu.x }}
                  >
                    {/* Reply */}
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                      onClick={() => handleReply(contextMenu.msg)}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
                      </svg>
                      Reply
                    </button>
                    {/* Forward */}
                    {contextMenu.msg.body && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        onClick={() => handleForward(contextMenu.msg)}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
                        </svg>
                        Forward
                      </button>
                    )}
                    {/* Copy */}
                    {contextMenu.msg.body && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        onClick={() => {
                          if (contextMenu.msg.body) navigator.clipboard.writeText(contextMenu.msg.body)
                          setContextMenu(null)
                        }}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                        </svg>
                        Copy
                      </button>
                    )}
                    {/* Edit (outgoing text only) */}
                    {contextMenu.msg.direction === 'OUTGOING' && contextMenu.msg.body && (
                      <button
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                        onClick={() => handleEditStart(contextMenu.msg)}
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
                        </svg>
                        Edit
                      </button>
                    )}
                    {/* Star / Unstar */}
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 flex items-center gap-3 text-gray-700"
                      onClick={() => handleToggleStar(contextMenu.msg)}
                    >
                      {contextMenu.msg.isStarred ? (
                        <>
                          <svg className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
                          Unstar
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" /></svg>
                          Star
                        </>
                      )}
                    </button>
                    {/* Divider */}
                    <div className="border-t border-gray-100 my-1"></div>
                    {/* Delete */}
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-red-50 flex items-center gap-3 text-red-500"
                      onClick={() => handleDeleteMessage(contextMenu.msg)}
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                      </svg>
                      Delete
                    </button>
                  </div>
                )}

                {/* Forward modal */}
                {forwardMsg && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={closeForwardModal}>
                    <div className="bg-white rounded-xl shadow-2xl w-[380px] flex flex-col" onClick={(e) => e.stopPropagation()}>
                      <div className="px-4 py-3 border-b flex items-center justify-between flex-shrink-0">
                        <h3 className="font-semibold text-gray-800">Forward message</h3>
                        <button onClick={closeForwardModal} className="text-gray-400 hover:text-gray-600">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                      {/* Preview of message being forwarded */}
                      <div className="px-4 py-2 bg-gray-50 border-b flex-shrink-0">
                        <p className="text-xs text-gray-500 mb-0.5">Forwarding:</p>
                        <p className="text-sm text-gray-700 truncate">{forwardMsg.body}</p>
                      </div>
                      {/* Success state */}
                      {forwardSent ? (
                        <div className="flex-1 flex flex-col items-center justify-center py-10 gap-3">
                          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
                            <svg className="w-7 h-7" style={{ color: '#16a34a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                          </div>
                          <p className="text-sm font-medium" style={{ color: '#15803d' }}>Message forwarded!</p>
                        </div>
                      ) : (
                        <>
                          {/* Search */}
                          <div className="px-3 py-2 border-b flex-shrink-0">
                            <input
                              type="text"
                              value={forwardSearch}
                              onChange={(e) => setForwardSearch(e.target.value)}
                              placeholder="Search groups..."
                              className="w-full px-3 py-1.5 rounded-lg bg-gray-100 text-sm outline-none focus:ring-1 focus:ring-green-400"
                              autoFocus
                            />
                          </div>
                          {/* Group list */}
                          <div className="overflow-y-auto max-h-[260px]">
                            {groups
                              .filter(g => g.id !== selectedGroup?.id && g.name.toLowerCase().includes(forwardSearch.toLowerCase()))
                              .map(g => (
                                <button
                                  key={g.id}
                                  className="w-full text-left px-4 py-2.5 flex items-center gap-3 border-b border-gray-50 transition-colors hover:bg-gray-50"
                                  style={forwardTarget === g.id ? { backgroundColor: '#f0fdf4', boxShadow: 'inset 0 0 0 1px #86efac' } : undefined}
                                  onClick={() => setForwardTarget(forwardTarget === g.id ? null : g.id)}
                                >
                                  <GroupAvatar groupName={g.name} pictureUrl={groupPictures[g.id]} size="sm" />
                                  <span className="text-sm font-medium text-gray-800 truncate flex-1">{g.name}</span>
                                  {forwardTarget === g.id && (
                                    <svg className="w-5 h-5 shrink-0" style={{ color: '#16a34a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                                  )}
                                </button>
                              ))
                            }
                          </div>
                          {/* Footer with Send button */}
                          <div className="px-4 py-3 border-t flex items-center justify-end gap-2 flex-shrink-0">
                            <button
                              onClick={closeForwardModal}
                              className="px-4 py-1.5 text-sm font-medium text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={executeForward}
                              disabled={!forwardTarget || forwardSending}
                              className="px-5 py-1.5 text-sm font-medium rounded-lg transition-colors flex items-center gap-2"
                              style={{
                                backgroundColor: forwardTarget && !forwardSending ? '#16a34a' : '#e5e7eb',
                                color: forwardTarget && !forwardSending ? '#ffffff' : '#9ca3af',
                                cursor: !forwardTarget || forwardSending ? 'not-allowed' : 'pointer',
                              }}
                            >
                              {forwardSending ? (
                                <>
                                  <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                                  Send
                                </>
                              )}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Reply-to banner */}
                {replyTo && (
                  <div className="flex-shrink-0 px-4 pt-2 pb-0" style={{ backgroundColor: '#f0f2f5' }}>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-t-lg border-l-4 border-green-500" style={{ backgroundColor: '#e2f7cb' }}>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-green-700">{getSenderName(replyTo)}</p>
                        <p className="text-xs text-gray-600 truncate">{replyTo.body || `[${replyTo.type}]`}</p>
                      </div>
                      <button onClick={() => setReplyTo(null)} className="shrink-0 text-gray-400 hover:text-gray-600">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                )}
                {/* Composer */}
                <div className="flex-shrink-0 px-4 py-2.5" style={{ backgroundColor: '#f0f2f5' }}>
                  {/* File preview */}
                  {pendingFile && (
                    <div className="flex items-center gap-2 mb-2 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                      {pendingFile.preview ? (
                        <img src={pendingFile.preview} alt="Preview" className="w-12 h-12 rounded object-cover" />
                      ) : (
                        <div className="w-12 h-12 rounded bg-surface-secondary flex items-center justify-center">
                          <svg className="w-6 h-6 text-txt-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-txt-primary truncate">{pendingFile.name}</p>
                        <p className="text-[10px] text-txt-tertiary">{(pendingFile.size / 1024).toFixed(1)} KB</p>
                      </div>
                      <button onClick={() => setPendingFile(null)} className="p-1 rounded hover:bg-surface-hover text-txt-tertiary hover:text-txt-primary">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                  <div className="flex items-center gap-2 relative">
                    {/* @-mention picker — group members matching the typed @token */}
                    {mentionQuery !== null && filteredParticipants.length > 0 && (
                      <div className="absolute left-0 right-0 bottom-full mb-2 max-h-60 overflow-y-auto rounded-lg border border-border bg-surface shadow-float z-30">
                        {filteredParticipants.map((p) => {
                          const pname = p.name || p.number || (p.id || '').split('@')[0]
                          const avatarJid = p.number ? `${p.number}@s.whatsapp.net` : p.id
                          const pic = participantPics[avatarJid]
                          return (
                            <button
                              key={p.id}
                              type="button"
                              onMouseEnter={() => loadParticipantPic(avatarJid)}
                              onClick={() => insertMention(p)}
                              className="flex items-center gap-3 w-full px-3 py-2 text-left hover:bg-surface-hover transition-colors"
                            >
                              <span className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-xs font-semibold overflow-hidden shrink-0">
                                {pic ? <img src={pic} alt="" className="w-full h-full object-cover" /> : (pname || '?').charAt(0).toUpperCase()}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block text-sm text-txt-primary truncate">{pname}</span>
                                {p.number && <span className="block text-[11px] text-txt-tertiary">+{p.number}</span>}
                              </span>
                              {p.isAdmin && <span className="text-[10px] text-txt-tertiary">admin</span>}
                            </button>
                          )
                        })}
                      </div>
                    )}
                    {/* File attachment button */}
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                    />
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={sendingMessage}
                      className="p-2.5 rounded-full transition-colors disabled:opacity-50"
                      style={{ color: '#54656f' }}
                      title="Attach file"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M18.375 12.739l-7.693 7.693a4.5 4.5 0 01-6.364-6.364l10.94-10.94A3 3 0 1119.5 7.372L8.552 18.32m.009-.01l-.01.01m5.699-9.941l-7.81 7.81a1.5 1.5 0 002.112 2.13" />
                      </svg>
                    </button>
                    <input
                      type="text"
                      value={messageText}
                      onChange={(e) => handleComposerChange(e.target.value)}
                      onKeyDown={(e) => {
                        if (mentionQuery !== null && filteredParticipants.length && e.key === 'Enter') { e.preventDefault(); insertMention(filteredParticipants[0]); return }
                        if (e.key === 'Escape' && mentionQuery !== null) { setMentionQuery(null); return }
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
                      }}
                      placeholder={pendingFile ? 'Add a caption...' : 'Type a message...'}
                      disabled={sendingMessage}
                      className="flex-1 px-4 py-2.5 rounded-lg border-0 text-sm outline-none transition-colors disabled:opacity-50"
                      style={{ backgroundColor: '#ffffff', color: '#111b21', boxShadow: 'none' }}
                    />
                    <button
                      onClick={handleSend}
                      disabled={sendingMessage || (!messageText.trim() && !pendingFile)}
                      className="p-2.5 rounded-full text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      style={{ backgroundColor: '#00a884' }}
                      title="Send"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              {/* ── Members info panel (right side) ────────────────── */}
              {showInfo && groupInfo && (
                <div className="w-[300px] border-l flex flex-col overflow-hidden shrink-0" style={{ borderColor: '#e9edef', backgroundColor: '#f0f2f5' }}>
                  {/* Panel header */}
                  <div className="flex items-center gap-3 px-4 py-3 shrink-0" style={{ backgroundColor: '#f0f2f5', borderBottom: '1px solid #e9edef' }}>
                    <button
                      onClick={() => setShowInfo(false)}
                      className="p-1 rounded-full transition-colors"
                      style={{ color: '#54656f' }}
                      title="Close panel"
                    >
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    <h3 className="text-sm font-semibold" style={{ color: '#111b21' }}>Group Info</h3>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {/* Group name & description section */}
                    <div className="px-5 py-4" style={{ backgroundColor: '#ffffff', borderBottom: '8px solid #f0f2f5' }}>
                      <div className="flex flex-col items-center text-center mb-3">
                        <div className="mb-2">
                          <GroupAvatar pictureUrl={groupPictures[selectedGroup.id]} groupName={selectedGroup.name} size="lg" />
                        </div>
                        <h4 className="text-base font-semibold" style={{ color: '#111b21' }}>{selectedGroup.name}</h4>
                        <p className="text-xs mt-0.5" style={{ color: '#667781' }}>
                          Group · {groupInfo.participants?.length || selectedGroup.participantsCount || 0} members
                        </p>
                      </div>
                      {groupInfo.description && (
                        <div className="mt-2">
                          <p className="text-xs font-medium mb-1" style={{ color: '#667781' }}>Description</p>
                          <p className="text-[13px] whitespace-pre-wrap" style={{ color: '#111b21', lineHeight: '1.4' }}>{linkifyText(groupInfo.description)}</p>
                        </div>
                      )}
                    </div>

                    {/* ── Tags section ─────────────────────────────── */}
                    <div className="px-5 py-3" style={{ backgroundColor: '#ffffff', borderBottom: '8px solid #f0f2f5' }}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-medium" style={{ color: '#008069' }}>Tags</p>
                        <button
                          onClick={() => setShowTagAssign(!showTagAssign)}
                          className="text-xs transition-colors"
                          style={{ color: '#008069' }}
                        >
                          {showTagAssign ? 'Done' : 'Edit'}
                        </button>
                      </div>
                      {/* Current tags */}
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(groupTagMap[selectedGroup.id] || []).length === 0 && !showTagAssign && (
                          <span className="text-xs" style={{ color: '#667781' }}>No tags assigned</span>
                        )}
                        {(groupTagMap[selectedGroup.id] || []).map(tag => (
                          <span
                            key={tag.tag_id}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
                            style={{ backgroundColor: tag.color + '20', color: tag.color, border: `1px solid ${tag.color}40` }}
                          >
                            {tag.name}
                            {showTagAssign && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handleToggleGroupTag(selectedGroup.id, tag.tag_id) }}
                                className="ml-0.5 hover:opacity-70"
                              >
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {/* Tag assignment dropdown */}
                      {showTagAssign && (
                        <div className="space-y-1 mt-2">
                          {allTags
                            .filter(t => !(groupTagMap[selectedGroup.id] || []).some(gt => gt.tag_id === t.id))
                            .map(tag => (
                              <button
                                key={tag.id}
                                onClick={() => handleToggleGroupTag(selectedGroup.id, tag.id)}
                                className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-left text-xs transition-colors hover:bg-gray-50"
                                style={{ color: '#111b21' }}
                              >
                                <span
                                  className="w-3 h-3 rounded-full shrink-0"
                                  style={{ backgroundColor: tag.color }}
                                />
                                {tag.name}
                                <svg className="w-3 h-3 ml-auto" style={{ color: '#667781' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                                </svg>
                              </button>
                            ))
                          }
                          {allTags.filter(t => !(groupTagMap[selectedGroup.id] || []).some(gt => gt.tag_id === t.id)).length === 0 && (
                            <p className="text-xs py-1" style={{ color: '#667781' }}>All tags assigned</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* ── Media Gallery section (collapsible) ──────── */}
                    <div style={{ backgroundColor: '#ffffff', borderBottom: '8px solid #f0f2f5' }}>
                      {/* Collapsed header row — always visible */}
                      <button
                        onClick={() => setMediaExpanded(!mediaExpanded)}
                        className="w-full flex items-center justify-between px-5 py-3 transition-colors hover:bg-gray-50/60"
                      >
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-medium" style={{ color: '#008069' }}>Media & Docs</p>
                          {mediaCounts.total > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#e7f8f3', color: '#008069' }}>
                              {mediaCounts.total}
                            </span>
                          )}
                          {/* Inline summary when collapsed */}
                          {!mediaExpanded && mediaCounts.total > 0 && (
                            <span className="text-[10px]" style={{ color: '#667781' }}>
                              {[mediaCounts.image && `${mediaCounts.image} photo${mediaCounts.image !== 1 ? 's' : ''}`, mediaCounts.video && `${mediaCounts.video} video${mediaCounts.video !== 1 ? 's' : ''}`, mediaCounts.document && `${mediaCounts.document} doc${mediaCounts.document !== 1 ? 's' : ''}`].filter(Boolean).join(', ')}
                            </span>
                          )}
                        </div>
                        <svg
                          className="w-4 h-4 transition-transform duration-200"
                          style={{ color: '#667781', transform: mediaExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                          fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                        </svg>
                      </button>

                      {/* Expandable content */}
                      {mediaExpanded && (
                        <div className="px-4 pb-3">
                          {/* Tab bar + View All */}
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex gap-1">
                              {(['image', 'video', 'document'] as const).map(tab => (
                                <button
                                  key={tab}
                                  onClick={() => setMediaTab(tab)}
                                  className="px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors"
                                  style={{
                                    backgroundColor: mediaTab === tab ? '#008069' : '#f0f2f5',
                                    color: mediaTab === tab ? '#ffffff' : '#667781',
                                  }}
                                >
                                  {tab === 'image' ? `Photos ${mediaCounts.image}` : tab === 'video' ? `Videos ${mediaCounts.video}` : `Docs ${mediaCounts.document}`}
                                </button>
                              ))}
                            </div>
                            {mediaCounts.total > 0 && (
                              <button
                                onClick={() => setShowFullGallery(true)}
                                className="text-[10px] font-medium transition-colors"
                                style={{ color: '#008069' }}
                              >
                                View All
                              </button>
                            )}
                          </div>

                          {/* Media content */}
                          {mediaLoading ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#008069', borderTopColor: 'transparent' }} />
                            </div>
                          ) : mediaItems.length === 0 ? (
                            <p className="text-[11px] text-center py-3" style={{ color: '#667781' }}>
                              No {mediaTab === 'image' ? 'photos' : mediaTab === 'video' ? 'videos' : 'documents'} yet
                            </p>
                          ) : mediaTab === 'document' ? (
                            <div className="space-y-0.5 max-h-[160px] overflow-y-auto">
                              {mediaItems.slice(0, 8).map(item => {
                                const meta = typeof item.metadata === 'string' ? JSON.parse(item.metadata || '{}') : (item.metadata || {})
                                const fname = meta.docFilename || meta.filename || 'Document'
                                return (
                                  <a
                                    key={item.id}
                                    href={item.media_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-50 transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5 shrink-0" style={{ color: '#667781' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                                    </svg>
                                    <span className="text-[10px] truncate flex-1" style={{ color: '#111b21' }}>{fname}</span>
                                    <svg className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100" style={{ color: '#667781' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                                    </svg>
                                  </a>
                                )
                              })}
                            </div>
                          ) : (
                            <div className="grid grid-cols-3 gap-1 max-h-[160px] overflow-y-auto">
                              {mediaItems.slice(0, 9).map(item => (
                                <div key={item.id} className="relative aspect-square rounded overflow-hidden cursor-pointer group" onClick={() => mediaTab === 'image' ? setLightboxUrl(item.media_url) : window.open(item.media_url, '_blank')}>
                                  {mediaTab === 'image' ? (
                                    <img src={item.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                                  ) : (
                                    <div className="w-full h-full bg-black flex items-center justify-center">
                                      <video src={item.media_url} className="w-full h-full object-cover" muted preload="metadata" />
                                      <div className="absolute inset-0 flex items-center justify-center">
                                        <svg className="w-6 h-6 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                                          <path d="M8 5v14l11-7z" />
                                        </svg>
                                      </div>
                                    </div>
                                  )}
                                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Members list section */}
                    <div className="px-4 py-3" style={{ backgroundColor: '#ffffff' }}>
                      <p className="text-xs font-medium px-1 mb-2" style={{ color: '#008069' }}>
                        {groupInfo.participants?.length || 0} members
                      </p>
                      <div className="space-y-0.5">
                        {/* Sort: admins first, then alphabetical */}
                        {groupInfo.participants
                          ?.slice()
                          .sort((a, b) => {
                            if (a.isSuperAdmin && !b.isSuperAdmin) return -1
                            if (!a.isSuperAdmin && b.isSuperAdmin) return 1
                            if (a.isAdmin && !b.isAdmin) return -1
                            if (!a.isAdmin && b.isAdmin) return 1
                            return (a.name || a.number || '').localeCompare(b.name || b.number || '')
                          })
                          .map((p) => (
                            <div key={p.id} className="flex items-center gap-3 px-1 py-2 rounded-lg transition-colors hover:bg-gray-50">
                              <div
                                className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold shrink-0"
                                style={{ backgroundColor: '#dfe5e7', color: '#667781' }}
                              >
                                {(p.name || '').charAt(0)?.toUpperCase() || (p.number ? p.number.replace(/^\+/, '').charAt(0) : '?')}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <p className="text-[13px] font-medium truncate" style={{ color: '#111b21' }}>
                                    {p.name || `+${p.number}`}
                                  </p>
                                  {(p.isSuperAdmin || p.isAdmin) && (
                                    <span
                                      className="text-[10px] font-medium px-1.5 py-0.5 rounded shrink-0"
                                      style={{
                                        backgroundColor: p.isSuperAdmin ? '#d1f4cc' : '#e8f5e9',
                                        color: '#008069',
                                      }}
                                    >
                                      {p.isSuperAdmin ? 'Owner' : 'Admin'}
                                    </span>
                                  )}
                                </div>
                                {p.number && p.name && (
                                  <p className="text-[11px] truncate" style={{ color: '#667781' }}>
                                    +{p.number}
                                  </p>
                                )}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Image Lightbox ──────────────────────────────────────────── */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
            onClick={() => setLightboxUrl(null)}
          >
            <svg className="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* ── Full-Page Media Gallery Overlay ────────────────────────────── */}
      {showFullGallery && selectedGroup && (
        <div className="fixed inset-0 z-50 flex flex-col" style={{ backgroundColor: '#ffffff' }}>
          {/* Gallery header */}
          <div className="flex items-center gap-3 px-5 py-3 shrink-0" style={{ backgroundColor: '#008069' }}>
            <button
              onClick={() => setShowFullGallery(false)}
              className="p-1 rounded-full text-white/80 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
              </svg>
            </button>
            <h3 className="text-sm font-semibold text-white">Media Gallery — {selectedGroup.name}</h3>
          </div>

          {/* Tab bar */}
          <div className="flex gap-2 px-5 py-3 border-b" style={{ borderColor: '#e9edef' }}>
            {(['image', 'video', 'document'] as const).map(tab => (
              <button
                key={tab}
                onClick={() => setMediaTab(tab)}
                className="px-4 py-1.5 rounded-full text-xs font-medium transition-colors"
                style={{
                  backgroundColor: mediaTab === tab ? '#008069' : '#f0f2f5',
                  color: mediaTab === tab ? '#ffffff' : '#667781',
                }}
              >
                {tab === 'image' ? `Photos (${mediaCounts.image})` : tab === 'video' ? `Videos (${mediaCounts.video})` : `Documents (${mediaCounts.document})`}
              </button>
            ))}
          </div>

          {/* Gallery content */}
          <div className="flex-1 overflow-y-auto p-5">
            {mediaLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#008069', borderTopColor: 'transparent' }} />
              </div>
            ) : mediaItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20" style={{ color: '#667781' }}>
                <svg className="w-16 h-16 mb-3 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v13.5A1.5 1.5 0 003.75 21z" />
                </svg>
                <p className="text-sm">No {mediaTab === 'image' ? 'photos' : mediaTab === 'video' ? 'videos' : 'documents'} in this group</p>
              </div>
            ) : mediaTab === 'document' ? (
              <div className="max-w-2xl mx-auto space-y-1">
                {mediaItems.map(item => {
                  const meta = typeof item.metadata === 'string' ? JSON.parse(item.metadata || '{}') : (item.metadata || {})
                  const fname = meta.docFilename || meta.filename || 'Document'
                  const fsize = meta.docFilesize ? `${(meta.docFilesize / 1024).toFixed(0)} KB` : ''
                  const date = item.timestamp ? new Date(item.timestamp * 1000).toLocaleDateString() : ''
                  return (
                    <a
                      key={item.id}
                      href={item.media_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: '#f0f2f5' }}>
                        <svg className="w-5 h-5" style={{ color: '#667781' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate" style={{ color: '#111b21' }}>{fname}</p>
                        <p className="text-xs" style={{ color: '#667781' }}>
                          {[item.sender_name, fsize, date].filter(Boolean).join(' · ')}
                        </p>
                      </div>
                      <svg className="w-4 h-4 shrink-0" style={{ color: '#667781' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                      </svg>
                    </a>
                  )
                })}
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 gap-1.5">
                {mediaItems.map(item => (
                  <div
                    key={item.id}
                    className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group"
                    onClick={() => mediaTab === 'image' ? setLightboxUrl(item.media_url) : window.open(item.media_url, '_blank')}
                  >
                    {mediaTab === 'image' ? (
                      <img src={item.media_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    ) : (
                      <div className="w-full h-full bg-black relative">
                        <video src={item.media_url} className="w-full h-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <svg className="w-10 h-10 text-white opacity-80" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M8 5v14l11-7z" />
                          </svg>
                        </div>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    <div className="absolute bottom-0 left-0 right-0 px-1.5 py-1 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-[10px] text-white truncate">{item.sender_name}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tag Manager Modal ──────────────────────────────────────── */}
      {showTagManager && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[420px] max-h-[80vh] flex flex-col overflow-hidden">
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <h3 className="text-base font-semibold text-txt-primary">Manage Group Tags</h3>
              <button onClick={() => { setShowTagManager(false); setEditingTag(null) }} className="p-1 rounded-lg hover:bg-surface-hover text-txt-secondary">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Create new tag */}
            <div className="px-5 py-3 border-b border-border">
              <p className="text-xs font-medium text-txt-secondary mb-2">Create new tag</p>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={newTagColor}
                  onChange={(e) => setNewTagColor(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-border"
                  title="Tag color"
                />
                <input
                  type="text"
                  placeholder="Tag name..."
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateTag()}
                  className="flex-1 px-3 py-1.5 rounded-lg bg-surface-secondary border border-border text-sm text-txt-primary placeholder-txt-tertiary outline-none focus:border-brand-500"
                />
                <button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim()}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-40"
                  style={{ backgroundColor: '#008069' }}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Existing tags list */}
            <div className="flex-1 overflow-y-auto px-5 py-3">
              {allTags.length === 0 ? (
                <p className="text-sm text-txt-tertiary text-center py-6">No tags yet. Create one above.</p>
              ) : (
                <div className="space-y-1.5">
                  {allTags.map(tag => (
                    <div key={tag.id} className="flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-surface-hover transition-colors group">
                      {editingTag?.id === tag.id ? (
                        <>
                          <input
                            type="color"
                            value={editingTag.color}
                            onChange={(e) => setEditingTag({ ...editingTag, color: e.target.value })}
                            className="w-6 h-6 rounded cursor-pointer border border-border"
                          />
                          <input
                            type="text"
                            value={editingTag.name}
                            onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                            onKeyDown={(e) => e.key === 'Enter' && handleUpdateTag(editingTag)}
                            className="flex-1 px-2 py-1 rounded border border-border text-sm text-txt-primary outline-none focus:border-brand-500"
                            autoFocus
                          />
                          <button onClick={() => handleUpdateTag(editingTag)} className="text-xs font-medium" style={{ color: '#008069' }}>Save</button>
                          <button onClick={() => setEditingTag(null)} className="text-xs text-txt-tertiary">Cancel</button>
                        </>
                      ) : (
                        <>
                          <span className="w-4 h-4 rounded-full shrink-0" style={{ backgroundColor: tag.color }} />
                          <span className="flex-1 text-sm text-txt-primary font-medium">{tag.name}</span>
                          <button
                            onClick={() => setEditingTag({ ...tag })}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-surface-secondary text-txt-tertiary transition-all"
                            title="Edit tag"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteTag(tag.id)}
                            className="p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-red-50 text-txt-tertiary hover:text-error transition-all"
                            title="Delete tag"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
